import React from 'react'
import { useState } from 'react'
import { useFormik, Form, FormikProvider } from 'formik'
import { Link as RouterLink } from 'react-router-dom'
// material
import {
  Alert,
  Checkbox,
  InputAdornment,
  Box,
  Link,
  Typography,
} from '@material-ui/core'
import { LoadingButton } from '@material-ui/lab'
// hooks
import useIsMountedRef from '../../../hooks/useIsMountedRef'
import { authenticationLoginUserAction } from '../../../reducers/authentication/action'
import { Icon } from '@iconify/react'
import {
  CoreIconButton,
  CoreTextField,
} from 'src/components/core'

// ----------------------------------------------------------------------

const LoginForm = props => {
  const isMountedRef = useIsMountedRef()
  const [showPassword, setShowPassword] = useState(false)

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: true,
    },
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        const { dispatch } = props

        const data = {
          UserName: values.email,
          Password: values.password,
        }
        dispatch(authenticationLoginUserAction(data))

        if (isMountedRef.current) {
          setSubmitting(false)
        }
      } catch (error) {
        console.error(error)
        resetForm()
        if (isMountedRef.current) {
          setSubmitting(false)
          setErrors({ afterSubmit: error.message })
        }
      }
    },
  })

  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    getFieldProps,
  } = formik

  const handleShowPassword = () => {
    setShowPassword(show => !show)
  }

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Box>
          {errors.afterSubmit && (
            <Alert severity="error">{errors.afterSubmit}</Alert>
          )}

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Email Address
          </Typography>
          <CoreTextField
            fullWidth
            marginTop={false}
            margin="none"
            autoComplete="username"
            type="email"
            placeholder="Enter your email address"
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
            sx={{ m: 0, mt: 0 }}
          />

          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
            Password
          </Typography>
          <CoreTextField
            fullWidth
            marginTop={false}
            margin="none"
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <CoreIconButton
                    onClick={handleShowPassword}
                    edge="end"
                    color="dark"
                    variant="text"
                  >
                    {showPassword ? (
                      <Icon icon="mdi:eye" />
                    ) : (
                      <Icon icon="mdi:eye-off" />
                    )}
                  </CoreIconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
            sx={{ m: 0, mt: 0 }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                {...getFieldProps('remember')}
                checked={values.remember}
                sx={{ p: 0, mr: 1, '&:hover': { backgroundColor: 'transparent' }, '&.Mui-focusVisible': { backgroundColor: 'transparent' }, '&.Mui-checked': { color: 'primary.main' } }}
                disableRipple
                disableFocusRipple
                disableTouchRipple
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Remember me
              </Typography>
            </Box>
            <Link
              component={RouterLink}
              variant="subtitle2"
              to={'/forgot-password'}
              sx={{
                color: 'var(--mcg-gray, #919EAB)',
                fontFamily: 'Inter',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textDecoration: 'none',
              }}
            >
              Forgot your password?
            </Link>
          </Box>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={!values.email || !values.password}
            sx={{
              height: '48px',
              padding: '15px 22px',
              backgroundColor: values.email && values.password ? '#015ffb' : '#919eab',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: values.email && values.password ? '#0151D5' : '#919eab',
                boxShadow: 'none',
              },
            }}
          >
            Continue
          </LoadingButton>
        </Box>

        <Box sx={{ marginTop: '16px', textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Don't have an account?{' '}
            <Link
              underline="none"
              variant="subtitle2"
              href="/register"
              sx={{
                color: '#015ffb',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Get Started
            </Link>
          </Typography>
        </Box>
      </Form>
    </FormikProvider>
  )
}

export default LoginForm
